import { keepPreviousData, useQuery } from '@tanstack/react-query';

import type { operations } from '@molchanovs/api-contract';
import { createQuery } from '@shared/lib/query-helpers';
import { invoiceDateBoundary, type InvoiceFilters } from '../model/mock-invoice-filters';
import type { InvoiceProductSelection } from '../model/mock-invoice-product-options';
import type { InvoiceListRow, InvoiceSortSelection, InvoiceTimeZone } from '../model/types';

export type InvoicePaymentsResponse =
  operations['getApiV1Payments']['responses'][200]['content']['application/json'];
export type InvoicePaymentDetailResponse =
  operations['getApiV1PaymentsByPaymentId']['responses'][200]['content']['application/json'];
type InvoicePaymentsRequest = NonNullable<operations['getApiV1Payments']['parameters']['query']>;

export interface InvoicePaymentsSelection {
  cursor?: string;
  filters: InvoiceFilters;
  productSelection: InvoiceProductSelection;
  q?: string;
  selectedOwner?: string;
  sort: InvoiceSortSelection;
  timeZone: InvoiceTimeZone;
}

const PAGE_SIZE = 10;

function normalizedDecimal(value: string | undefined): string | undefined {
  const normalized = value?.trim().replace(',', '.');
  return normalized || undefined;
}

function dateBoundaryIso(
  value: string | undefined,
  end: boolean,
  timeZone: InvoiceTimeZone
): string | undefined {
  if (!value) return undefined;
  const boundary = invoiceDateBoundary(value, end, timeZone);
  return Number.isFinite(boundary) ? new Date(boundary).toISOString() : undefined;
}

export function toInvoicePaymentsRequest(
  selection: InvoicePaymentsSelection
): InvoicePaymentsRequest {
  const { filters, productSelection } = selection;
  return {
    limit: PAGE_SIZE,
    sort:
      selection.sort.field === 'amount'
        ? selection.sort.direction === 'desc'
          ? '-amountPaid'
          : 'amountPaid'
        : selection.sort.direction === 'desc'
          ? '-paymentDate'
          : 'paymentDate',
    ...(selection.cursor ? { cursor: selection.cursor } : {}),
    ...(selection.q?.trim() ? { q: selection.q.trim() } : {}),
    ...(normalizedDecimal(filters.amountEqualTo)
      ? { amountEqualTo: normalizedDecimal(filters.amountEqualTo) }
      : {}),
    ...(normalizedDecimal(filters.amountGreaterThan)
      ? { amountGreaterThan: normalizedDecimal(filters.amountGreaterThan) }
      : {}),
    ...(normalizedDecimal(filters.amountLessThan)
      ? { amountLessThan: normalizedDecimal(filters.amountLessThan) }
      : {}),
    ...(filters.type ? { paymentType: filters.type } : {}),
    ...(filters.origin ? { origin: filters.origin } : {}),
    ...(dateBoundaryIso(filters.dateFrom, false, selection.timeZone)
      ? { paymentDateFrom: dateBoundaryIso(filters.dateFrom, false, selection.timeZone) }
      : {}),
    ...(dateBoundaryIso(filters.dateTo, true, selection.timeZone)
      ? { paymentDateTo: dateBoundaryIso(filters.dateTo, true, selection.timeZone) }
      : {}),
    ...(productSelection.itemType ? { itemType: productSelection.itemType } : {}),
    ...(productSelection.membershipType
      ? { membershipTypeId: productSelection.membershipType }
      : {}),
    ...(productSelection.btProgram ? { trainingProgramId: productSelection.btProgram } : {}),
    ...(selection.selectedOwner ? { userProfileId: selection.selectedOwner } : {}),
  };
}

export const invoicePaymentsKeys = {
  all: ['invoice-payments'] as const,
  page: (viewerAccessScope: string, request: InvoicePaymentsRequest) =>
    [...invoicePaymentsKeys.all, viewerAccessScope, request] as const,
  detail: (viewerAccessScope: string, paymentId: string) =>
    [...invoicePaymentsKeys.all, viewerAccessScope, 'detail', paymentId] as const,
};

export function invoicePaymentsQuery(
  viewerAccessScope: string,
  selection: InvoicePaymentsSelection
) {
  const request = toInvoicePaymentsRequest(selection);
  return createQuery<InvoicePaymentsResponse>({
    path: '/api/v1/payments',
    queryKey: invoicePaymentsKeys.page(viewerAccessScope, request),
    requestOptions: { params: request },
  });
}

export function useInvoicePayments(
  viewerAccessScope: string,
  selection: InvoicePaymentsSelection,
  enabled: boolean
) {
  return useQuery({
    ...invoicePaymentsQuery(viewerAccessScope, selection),
    enabled: enabled && viewerAccessScope.length > 0,
    gcTime: 5 * 60 * 1_000,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    retry: false,
    staleTime: 15_000,
  });
}

export function invoicePaymentDetailQuery(viewerAccessScope: string, paymentId: string) {
  return createQuery<InvoicePaymentDetailResponse>({
    path: `/api/v1/payments/${encodeURIComponent(paymentId)}`,
    queryKey: invoicePaymentsKeys.detail(viewerAccessScope, paymentId),
  });
}

export function useInvoicePaymentDetail(viewerAccessScope: string, paymentId: string) {
  return useQuery({
    ...invoicePaymentDetailQuery(viewerAccessScope, paymentId),
    enabled: viewerAccessScope.length > 0 && paymentId.length > 0,
    gcTime: 5 * 60 * 1_000,
    refetchOnWindowFocus: false,
    retry: false,
    staleTime: 15_000,
  });
}

export function toInvoiceListRow(
  payment: InvoicePaymentsResponse['data']['items'][number] | InvoicePaymentDetailResponse['data']
): InvoiceListRow {
  return {
    id: payment.id,
    accountName: payment.accountDisplayName ?? '—',
    amountPaid: Number(payment.amountPaidUsd),
    cartId: payment.cart?.id ?? null,
    cartItemCount: payment.cart?.itemCount ?? 0,
    courseId: payment.courseId,
    hasExpandableContent: payment.hasExpandableContent,
    itemTypes: payment.itemTypes,
    paymentDate: payment.paymentDate,
    processorText: payment.processorText,
    paymentTypeLabel: payment.paymentType.label,
    originLabel: payment.origin?.label ?? null,
    itemTypeLabels: payment.itemTypes.map(({ label }) => label),
    status: payment.status,
    legacyDescription: payment.legacyDescription,
    membershipId: payment.membershipId,
    registrationIds: payment.registrationIds,
    trainingProgramIds: payment.trainingProgramIds,
  };
}

export function toInvoiceListRows(response: InvoicePaymentsResponse | undefined): InvoiceListRow[] {
  return (response?.data.items ?? []).map(toInvoiceListRow);
}

export function invoiceTotalAmountCents(
  response: InvoicePaymentsResponse | undefined
): number | undefined {
  if (!response) return undefined;
  return Math.round(Number(response.data.summary.totalAmountUsd) * 100);
}

export const invoicePaymentsPageSize = PAGE_SIZE;
